import { assertUnreachable } from '@disy/lib/custom-error';

// <GENERATED-CODE>
// Generated from Java using 'npm run generate:ts'

import i18n from './attribute-type.properties';

const ATTRIBUTE_TYPE_VALUES = [
  'STRING',
  'INT',
  'LONG',
  'DOUBLE',
  'BIGDECIMAL',
  'DATE',
  'DURATION',
  'GEOMETRY',
  'IPADDRESS',
  'LOB',
  'URL',
  'UNKNOWN',
] as const;

/** @see net/disy/lib/attribute/AttributeType.java */
export type AttributeType = (typeof ATTRIBUTE_TYPE_VALUES)[number];

export const AttributeType = {
  values: ATTRIBUTE_TYPE_VALUES as readonly AttributeType[],
  i18n: (value: AttributeType) => i18n(`<${value}>`),
} as const;

// </GENERATED-CODE>

export function isAttributeType(value: string): value is AttributeType {
  return ATTRIBUTE_TYPE_VALUES.includes(value as never);
}

const FLOATING_POINT_NUMERIC_TYPES = new Set<'DOUBLE' | 'BIGDECIMAL'>(['DOUBLE', 'BIGDECIMAL']);

export function isFloatingPoint(type: AttributeType) {
  return (FLOATING_POINT_NUMERIC_TYPES as Set<AttributeType>).has(type);
}

export type NumericAttributeType = Extract<AttributeType, 'INT' | 'LONG' | 'DOUBLE' | 'BIGDECIMAL'>;

export const NUMERIC_TYPES = new Set<NumericAttributeType>(['INT', 'LONG', ...FLOATING_POINT_NUMERIC_TYPES]);

export function isNumeric(type: AttributeType): type is NumericAttributeType {
  return (NUMERIC_TYPES as Set<AttributeType>).has(type);
}

const FORMATTABLE_TYPES = new Set<AttributeType>([...NUMERIC_TYPES, 'DATE', 'DURATION']);

export function isFormattable(type: AttributeType) {
  return FORMATTABLE_TYPES.has(type);
}

export const FUNCTION_ATTRIBUTE_DATA_TYPES = [
  'STRING',
  'INT',
  'LONG',
  'DOUBLE',
  'BIGDECIMAL',
  'DATE',
  'DURATION',
  'GEOMETRY',
  'URL',
] as const satisfies AttributeType[];

export function isClassifiable(type: AttributeType) {
  return CLASSIFIABLE_TYPES.has(type);
}

const CLASSIFIABLE_TYPES = new Set<AttributeType>([...NUMERIC_TYPES, 'DATE', 'STRING', 'LOB', 'URL']);

/**
 * Returns a sample value to be formatted on the server.
 *
 * @param attributeType - The attribute type to get a sample for
 * @return The sample value
 * @see AttributeValueJsSerializer.java
 */
export function getExampleValue(attributeType: AttributeType) {
  switch (attributeType) {
    case 'STRING':
      return '...';
    case 'DOUBLE':
      return 1234.56;
    case 'INT':
      return 1234;
    case 'BIGDECIMAL':
      return '1234.56';
    case 'LONG':
      return '1234';
    case 'DATE':
      return new Date().toISOString();
    case 'DURATION':
      return 1000 * 60 * 60 * 12; // 12h in [ms]
  }
  throw new Error(`Unsupported attribute type "${attributeType}"`);
}

const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';
const DEFAULT_DOUBLE_FORMAT = '#,##0.00';
const DEFAULT_INT_FORMAT = '#,##0';
const DEFAULT_DURATION_FORMAT = 'Auto';

/**
 * This function must be aligned with backend logic.
 * @see net.disy.lib.attribute.AttributeType.getDefaultFormat
 */
export function getDefaultFormat(type: AttributeType) {
  switch (type) {
    case 'STRING':
    case 'IPADDRESS':
    case 'LOB':
    case 'URL':
    case 'GEOMETRY':
    case 'UNKNOWN':
      return null;
    case 'BIGDECIMAL':
    case 'DOUBLE':
      return DEFAULT_DOUBLE_FORMAT;
    case 'DATE':
      return DEFAULT_DATE_FORMAT;
    case 'DURATION':
      return DEFAULT_DURATION_FORMAT;
    case 'INT':
    case 'LONG':
      return DEFAULT_INT_FORMAT;
  }
  assertUnreachable(type);
}
